import { FC } from 'react';

import { useOcFabWidgetOptions } from '../../contexts/OcFabWidgetOptionsContext';
import {
  LoginButton,
  LoginIcon,
  LoginMessage,
  LoginRequiredContainer,
  LoginTitle,
} from './OcLoginRequired.styled';

export interface OcLoginRequiredProps {
  loginUrl: string;
}

const withReturnUrlIfMissingOrEmpty = (loginUrl: string): string => {
  if (typeof window === 'undefined') {
    return loginUrl;
  }

  const currentPageUrl = window.location.href;

  try {
    const url = new URL(loginUrl, window.location.origin);
    const matches: Array<{ key: string; value: string }> = [];
    url.searchParams.forEach((value, key) => {
      if (key.toLowerCase() === 'returnurl') {
        matches.push({ key, value });
      }
    });

    // If any ReturnUrl variant already has a value, leave it as-is.
    const hasNonEmptyReturnUrl = matches.some(
      (m) => m.value != null && m.value.trim() !== '',
    );

    if (!hasNonEmptyReturnUrl) {
      const keyToUse = matches[0]?.key ?? 'ReturnUrl';

      // Avoid multiple ReturnUrl variants; keep just one key (preserving casing if it existed).
      for (const m of matches) {
        url.searchParams.delete(m.key);
      }
      url.searchParams.set(keyToUse, currentPageUrl);
    }

    // Preserve relative URLs if the input was relative.
    const isAbsolute =
      loginUrl.startsWith('http://') ||
      loginUrl.startsWith('https://') ||
      loginUrl.startsWith('//');
    if (!isAbsolute) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  } catch {
    // If URL parsing fails, fall back to the original loginUrl.
    return loginUrl;
  }
};

export const OcLoginRequired: FC<OcLoginRequiredProps> = ({
  loginUrl,
}) => {
  const { loginTitle, loginHelpText, loginButtonText } = useOcFabWidgetOptions();
  const href = withReturnUrlIfMissingOrEmpty(loginUrl);

  return (
    <LoginRequiredContainer>
      <LoginIcon
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
        <polyline points="10 17 15 12 10 7" />
        <line x1="15" y1="12" x2="3" y2="12" />
      </LoginIcon>
      <LoginTitle>{loginTitle}</LoginTitle>
      <LoginMessage>{loginHelpText}</LoginMessage>
      <LoginButton href={href}>{loginButtonText}</LoginButton>
    </LoginRequiredContainer>
  );
};

